import React, { FC, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { Form, FormikProvider } from 'formik';
import Loader from 'components/UI/Loader';
import { FiCheckCircle } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import Button from 'components/UI/Buttons/buttons';
import Input from 'components/UI/Input/input';
import Card from 'components/UI/Card';
import { ReactComponent as ShowPassSvg } from '../../../assets/show_pass.svg';
import { ReactComponent as HidePassSvg } from '../../../assets/hide_pass.svg';
import css from '../styles.module.scss';
import { useSignInDetail } from '../hooks/useSignInDetail';
import LogoType from '../LogoType';

// Ленивая загрузка конкретной иконки
const FiAlertCircle = lazy(() =>
  import('react-icons/fi').then((module) => ({
    default: module.FiAlertCircle,
  })),
);

const SignIn: FC = () => {
  const { t } = useTranslation();

  const {
    isRegistration,
    isLoading,
    notificationTitle,
    confirm,
    error,
    errors,
    touched,
    submitCount,
    values,
    handleChange,
    handleBlur,
    formikContext,
    authLoading,
    openPass,
    handleToggleOpenPass,
  } = useSignInDetail();

  if (isLoading) {
    return (
      <div className={css.form__loading}>
        <Loader transparent />
      </div>
    );
  }

  return (
    <Card noHover className={css.form__card}>
      <FormikProvider value={formikContext}>
        <Form className={css.form__block}>
          <Suspense fallback={<></>}>
            <LogoType />
          </Suspense>
          <h1 className={css.form__title}>{t('form.common.signin')}</h1>
          {confirm && (
            <div className={css.form__confirm}>
              <FiCheckCircle size={24} />
              <span>{notificationTitle}</span>
            </div>
          )}
          {!!error && (
            <div className={css.form__error}>
              <FiAlertCircle size={16} color="red" />
              <span>{error}</span>
            </div>
          )}
          <Input
            label={t('form.common.email')}
            error={!!submitCount && !!touched.email && !!errors.email}
            errortext={errors.email}
            inputAttributes={{
              name: 'email',
              placeholder: 'admin@gmail.com',
              value: values.email,
              onChange: handleChange,
              onBlur: handleBlur,
            }}
          />
          <div className={css.form__pass}>
            <Input
              label={t('form.common.password')}
              icon={
                openPass ? (
                  <HidePassSvg onClick={handleToggleOpenPass} />
                ) : (
                  <ShowPassSvg onClick={handleToggleOpenPass} />
                )
              }
              icon_classname={css.form__pass_icon}
              error={!!submitCount && !!touched.password && !!errors.password}
              errortext={errors.password}
              inputAttributes={{
                name: 'password',
                type: openPass ? 'text' : 'password',
                value: values.password,
                onChange: handleChange,
                onBlur: handleBlur,
                placeholder: t('form.common.password'),
              }}
            />
            <Link to="/auth/forgot-password" className={css.form__forget}>
              {t('login_page.forgotPass')}
            </Link>
          </div>
          <Button
            title={t('form.common.signin')}
            buttonAttributes={{
              type: 'submit',
              disabled: authLoading,
              className: css.form__submit,
            }}
          />
          {isRegistration && (
            <div className={css.form__new}>
              <span>{t('form.common.new_user')}</span>
              <Link to="/auth/sign-up">{t('form.common.register')}</Link>
            </div>
          )}
        </Form>
      </FormikProvider>
    </Card>
  );
};

export default SignIn;
